/*
 * DictionaryLoader — On-Demand-Lazy-Loading von Hunspell-Woerterbuechern.
 *
 * Strategie:
 *   1. IndexedDB-Cache pruefen (Store 'dictionaries').
 *   2. Cache-Hit  → Buffers sofort zurueckgeben. Im Hintergrund einen HEAD-
 *      Request feuern, der via ETag prueft, ob serverseitig eine neuere Version
 *      liegt. Wenn ja: re-fetch und Cache-Update — aber NIEMALS waehrend einer
 *      Session den hot Buffer austauschen, das Update wird erst beim naechsten
 *      App-Start aktiv.
 *   3. Cache-Miss → fetch parallel /dictionaries/{lang}/index.aff und .dic,
 *      Buffers in IDB ablegen, Buffers zurueckgeben.
 *   4. Netzwerk-Fehler ohne Cache → Error. Aufrufer (Manager) markiert Sprache
 *      als nicht verfuegbar und zeigt einen Toast.
 *
 * Der Loader haelt selbst KEINEN Memory-Cache — der Worker behaelt die
 * geparsten Hunspell-Instanzen, der Loader liefert nur Buffers on-demand.
 */

const DEFAULT_BASE_PATH = '/dictionaries'

// Plausibility-Schwellen fuer den Cache. Falls jemals eine partielle Response
// (z.B. 206 Partial Content vom Service Worker oder Proxy) im Cache landet,
// wuerde Hunspell den Buffer parsen, fast keine Woerter erkennen und alles
// als Tippfehler markieren — ohne zu werfen. Diese Schranken sind absichtlich
// niedrig: das kleinste reale .dic (en) hat ~500 KB, das kleinste .aff einige KB.
const MIN_AFF_BYTES = 1024
const MIN_DIC_BYTES = 100 * 1024

export class DictionaryLoader {
    /**
     * @param {object} idbStore - IndexedDBStore-Instanz mit
     *        getDictionary/putDictionary-Methoden
     * @param {Function} [fetchImpl=fetch] - injectable fuer Tests
     * @param {string} [basePath='/dictionaries'] - URL-Praefix fuer .aff/.dic.
     *        In Production unter '/app/dictionaries' (Carbisdale liegt unter
     *        /app/), in Dev unter '/dictionaries' (Express-Static-Mount).
     */
    constructor(
        idbStore,
        fetchImpl = null,
        basePath = DEFAULT_BASE_PATH,
        minSizes = { aff: MIN_AFF_BYTES, dic: MIN_DIC_BYTES }
    ) {
        if (!idbStore) throw new Error('DictionaryLoader: idbStore required')
        this._store = idbStore
        // Default-fetch muss an die globale Umgebung gebunden bleiben — wenn
        // wir das native `fetch` als Method-Reference auf `this` speichern und
        // dann als `this._fetch(...)` aufrufen, kommt im Browser
        // "Illegal invocation" (fetch erwartet `this === window`).
        if (fetchImpl) {
            this._fetch = fetchImpl
        } else if (typeof fetch !== 'undefined') {
            this._fetch = (...args) => fetch(...args)
        } else {
            this._fetch = null
        }
        this._basePath = basePath.replace(/\/+$/, '')   // Trailing-Slash entfernen
        this._minAff = minSizes?.aff ?? MIN_AFF_BYTES
        this._minDic = minSizes?.dic ?? MIN_DIC_BYTES
    }

    /**
     * Laedt ein Woerterbuch (Cache-First).
     * @param {string} lang - z.B. 'de', 'en'
     * @returns {Promise<{aff:ArrayBuffer, dic:ArrayBuffer}>}
     */
    async load(lang) {
        const cached = await this._store.getDictionary(lang)
        if (cached && this._looksValid(cached)) {
            // Cache-Hit. Hintergrund-Validation feuern, Promise nicht awaiten.
            this._validateInBackground(cached).catch(() => { /* still ignorieren */ })
            return { aff: cached.aff, dic: cached.dic }
        }
        // Cache fehlt oder sieht korrupt aus (z.B. abgeschnittener Buffer aus
        // einer fruehen 206-Response). Eintrag wegwerfen und frisch holen.
        if (cached) {
            console.warn('[DictionaryLoader] cached', lang, 'looks corrupt — refetching',
                { aff: cached.aff?.byteLength, dic: cached.dic?.byteLength })
            await this._store.deleteDictionary(lang).catch(() => {})
        }
        return this._fetchAndCache(lang)
    }

    /**
     * Erzwingt einen Re-Fetch (z.B. wenn der Worker meldet, der Cache sei
     * korrupt). Der bestehende Cache-Eintrag wird ueberschrieben.
     * @param {string} lang
     * @returns {Promise<{aff:ArrayBuffer, dic:ArrayBuffer}>}
     */
    async forceRefresh(lang) {
        await this._store.deleteDictionary(lang).catch(() => {})
        return this._fetchAndCache(lang)
    }

    // ============================================================
    // intern
    // ============================================================

    async _fetchAndCache(lang) {
        if (!this._fetch) {
            throw new Error('DictionaryLoader: no fetch implementation available')
        }
        const affUrl = `${this._basePath}/${lang}/index.aff`
        const dicUrl = `${this._basePath}/${lang}/index.dic`

        const [affRes, dicRes] = await Promise.all([
            this._fetch(affUrl),
            this._fetch(dicUrl)
        ])
        // Strikt 200 verlangen — eine 206 (Partial Content) liefert nur einen
        // Teil-Buffer; Hunspell parst das ohne Error und liefert dann fast
        // keine Treffer. Lieber laut werfen, damit der Caller es loggt und
        // nicht stillschweigend Muell in den Cache faellt.
        if (affRes.status !== 200 || dicRes.status !== 200) {
            throw new Error(
                `Dictionary fetch failed for ${lang}: ` +
                `aff=${affRes.status} dic=${dicRes.status}`
            )
        }

        const [aff, dic] = await Promise.all([
            affRes.arrayBuffer(),
            dicRes.arrayBuffer()
        ])
        // Plausibilitaetscheck nach dem Lesen — schuetzt zusaetzlich gegen
        // truncated Bodies, falls eine Zwischenstelle (Proxy/SW) das Streamen
        // abbricht ohne Fehler.
        if (aff.byteLength < this._minAff || dic.byteLength < this._minDic) {
            throw new Error(
                `Dictionary too small for ${lang}: ` +
                `aff=${aff.byteLength} dic=${dic.byteLength}`
            )
        }
        // ETag vom .dic (groesseres File, aussagekraeftiger). aff aendert sich
        // praktisch nie unabhaengig vom dic.
        const etag = dicRes.headers?.get?.('etag') || ''

        await this._store.putDictionary({
            lang, aff, dic, etag, fetchedAt: Date.now()
        })

        return { aff, dic }
    }

    async _validateInBackground(cached) {
        // Wird nur fuer plausible Cache-Hits aufgerufen (load() filtert vorher).
        // Hier nur ETag-Vergleich — bei Mismatch laeuft refetch im Hintergrund.
        if (!this._fetch || !cached.etag) return
        const dicUrl = `${this._basePath}/${cached.lang}/index.dic`
        const res = await this._fetch(dicUrl, { method: 'HEAD' })
        if (!res.ok) return
        const remoteEtag = res.headers?.get?.('etag')
        if (!remoteEtag || remoteEtag === cached.etag) return
        // Versions-Mismatch: re-fetch laeuft im Hintergrund, der naechste
        // App-Start nutzt die neue Version automatisch (load() schaut wieder
        // im Cache nach).
        this._fetchAndCache(cached.lang).catch(() => {})
    }

    _looksValid(cached) {
        if (!cached || !cached.aff || !cached.dic) return false
        const affLen = cached.aff.byteLength
        const dicLen = cached.dic.byteLength
        if (typeof affLen !== 'number' || typeof dicLen !== 'number') return false
        return affLen >= this._minAff && dicLen >= this._minDic
    }
}

